<script>
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {useDonationSessionStore} from "../../stores/donationSession";
import HelcimPaymentButton from "../paymentProcessors/HelcimPaymentButton.vue";
import AccordionItem from "../components/accordion/AccordionItem.vue";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import StripePaymentButton from "../paymentProcessors/StripePaymentButton.vue";
import CurrencyInput from "../components/currencyInput/currencyInput.vue";
import BaseModal from "../components/BaseModal.vue";
import Icon from "../components/Icon.vue";
import Tooltip from "../components/tooltip.vue";
import {onMounted} from "vue";

export default {
  name: "DonationTipForm",
  components: {Tooltip, Icon, BaseModal, CurrencyInput, StripePaymentButton, AccordionItem, HelcimPaymentButton},
  setup() {
    // Stores
    const donationSessionStore = useDonationSessionStore();

    onMounted(() => {
      donationSessionStore.calculateDefaultCustomTip();
    });

    return {
      donationSessionStore,
    };
  },
  data() {
    return {
      showLearnMoreModal: false,
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    showCustomTipInput() {
      return this.donationSessionStore.donationForm.selectedTipOption === "custom";
    },
    // Calculates the max tip input allowed using the tipMaxPercent and current total - 10% roughly
    tipInputMax() {
      let subtotal = this.donationSessionStore.cartSubtotal;
      let maxPercent = this.donationSessionStore.donationForm.tipMaxPercent;

      // Subtotal is in cents, component expects decimal values
      return (subtotal / 100) * (maxPercent / 100);
    }
  },
  methods: {
    tipOption(option) {
      if (option === "custom") {
        return "Custom";
      }
      return `${option}%  (${ApplicationHelper.formatMoney((this.donationSessionStore.cartSubtotal * (option / 100)))})`;
    },
  }
};
</script>


<template>
  <div class="tip-form-container">
    <!--    Summary with Tip Option-->
    <div
      v-if="donationSessionStore.showTipOption"
      class="flex flex-col justify-between rounded mt-2"
    >
      <div
        class="flex flex-col md:flex-row p-4"
      >
        <div class="w-full md:w-2/3">
          <div class="flex flex-row">
            <div class="text-xl font-semibold">
              Protecting Generosity Together
            </div>
          </div>
          <div class="text-sm pt-2">
            <div class="font-semibold">
              Today, your gift is helping advance the mission of
              {{ donationSessionStore?.currentOrganization?.name }}.
            </div>
            <div class="mt-2">
              Your optional mission support helps ensure that churches and Christian organizations receive
              100% of their gifts and 100% reimbursement of processing costs, allowing more generosity to
              stay focused on ministry.
            </div>
            <div class="font-semibold mt-2">
              Thank you for helping protect, grow, and multiply generosity.
            </div>
          </div>

          <span
            class="text-xs text-blue-600 mt-4 cursor-pointer underline active:text-default_secondary"
            @click="showLearnMoreModal = true"
          >
            Learn more
          </span>
        </div>

        <div class="w-full md:w-1/3 md:ml-2 mt-2 md:mt-0">
          <label
            for="custom-fee-amount"
            class="text-xs mt-2"
          >
            Amount
          </label>
          <select
            v-model="donationSessionStore.donationForm.selectedTipOption"
            class="donation-fee-input mb-2"
          >
            <option
              v-for="option in donationSessionStore.donationForm.tipPercents"
              :key="option"
              :value="option"
            >
              {{ tipOption(option) }}
            </option>
          </select>


          <label
            v-if="showCustomTipInput"
            for="custom-fee-amount"
            class="text-xs"
          >
            Custom Amount
          </label>
          <currency-input
            v-if="showCustomTipInput"
            id="custom-fee-amount"
            v-model="donationSessionStore.donationForm.customTipAmount"
            max-overflow-message="Value cannot exceed 10% of your gift."
            prefix="$ "
            :max="tipInputMax"
            class="donation-fee-input"
          />
        </div>
      </div>
    </div>
  </div>
  <BaseModal
    v-if="showLearnMoreModal"
    size="small"
    @close="showLearnMoreModal = false"
  >
    <template #header-slot>
      <div class="flex flex-row justify-start">
        <img
          src="@/assets/logos/allgive_thumbnail.png"
          class="w-[50px] h-[50px] mr-4"
          alt="AllGive logo"
        >
        <h1 class="font-bold mt-2 ml-1">
          Join Us in Redefining Generosity!
        </h1>
      </div>
    </template>
    <div class="font-bold mt-4">
      Most giving platforms are built to process transactions. <a
      class="text-default_primary underline hover:text-default_primary"
      target="_blank"
      href="https://www.allgive.online"
    >AllGive</a> was built to protect, grow, and multiply generosity.
    </div>
    <div class="mt-4">
      Every year, churches and Christian organizations lose valuable ministry dollars
      to software subscriptions, platform fees, and payment processing
      costs—resources that could otherwise fuel discipleship, outreach, care, and
      Kingdom impact.
    </div>
    <div class="text-default_primary font-semibold mt-4">
      At AllGive, we believe generosity was never meant to be transactional.
    </div>
    <div class="mt-4">
      That's why we've created a different model—one designed to help churches
      and Christian organizations receive 100% of their gifts and 100%
      reimbursement of processing costs so more generosity stays focused on
      ministry.
    </div>
    <div class="mt-4">
      Your optional mission support helps expand this mission so more churches
      and Christian organizations can:
    </div>
    <ul class="modal-list mt-4">
      <li><strong>Protect Generosity</strong> by keeping more resources focused on ministry.</li>
      <li><strong>Grow Generosity</strong> by helping people take their next step in their generosity
        journey.</li>
      <li><strong>Multiply Generosity</strong> by increasing the impact generosity has on lives,
        communities, and the Kingdom.</li>
    </ul>
    <div class="mt-4">
      Together, we're helping ensure that generosity reaches the mission it was
      intended to support. <span class="text-default_primary font-semibold">Thank you for helping protect, grow, and multiply
      generosity.</span>
    </div>
    <button
      class="btn btn-filled justify-self-end mt-2"
      @click="showLearnMoreModal = false"
    >
      I Understand
    </button>
  </BaseModal>
</template>


<style scoped>
.tip-form-container {
  @apply text-black rounded-md bg-default_primary_lightest;
}

.donation-fee-input, :deep(.donation-fee-input) {
  @apply font-bold;
  @apply mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none sm:text-sm rounded-md;
}

h1 {
  font-size: 1.5em;
  margin-bottom: 20px;
}

ul {
  list-style-type: none;
  padding: 0;
}

li {
  margin-bottom: 10px;
}

strong {
  font-weight: bold;
}

.modal-list {
  list-style-type: disc;
  margin-left: 20px;
}
</style>
